import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { QUERY_CACHE_KEYS } from '@shared/api/queryKeys'
import { LOCALE_TO_BACKEND_HEADER, toAppLocale } from '@shared/lib'
import { useTicketedSocket } from '@shared/lib/useTicketedSocket'
import { useNotificationsUi } from './provider'
import { NotificationCategory, type Notification } from './types'

const NOTIFICATIONS_PATH = '/notifications/'

/** Пауза перед переподключением за снапшотом после живого пуша. Админу бэк
 *  шлёт пуши обо всех событиях платформы, и каждое переподключение — это новый
 *  тикет, хендшейк и полный список; без паузы пачка пушей превращалась в шторм.
 *  Переподключение одно на окно и случается в конце него, поэтому снапшот
 *  захватывает и пуши, пришедшие внутри окна. */
export const RECONNECT_COALESCE_MS = 3000

/** WS pushes sometimes arrive with a legacy shape: `notif_type: 'requests'`
 *  instead of `type_display: 'Requests'`, and without `id`/`created_at`. */
interface RawWsNotification extends Partial<Notification> {
  notif_type?: string
}

interface WsPayload {
  latest_notifications?: RawWsNotification[]
  notification?: RawWsNotification
  id?: number
}

const NOTIF_TYPE_TO_CATEGORY: Record<string, NotificationCategory> = {
  requests: NotificationCategory.Requests,
  users: NotificationCategory.Users,
  chats: NotificationCategory.Chats,
  payments: NotificationCategory.Payments,
  olympiads: NotificationCategory.Olympiads,
  tasks: NotificationCategory.Tasks,
  results: NotificationCategory.Results,
  certificates: NotificationCategory.Certificates,
}

/** Coerce a raw WS notification into the app shape. Returns null (and warns)
 *  only when the category can't be resolved — everything else is renderable. */
function normalizeNotification(raw: RawWsNotification): Notification | null {
  const typeDisplay =
    raw.type_display ??
    (raw.notif_type ? NOTIF_TYPE_TO_CATEGORY[raw.notif_type.toLowerCase()] : undefined)
  if (!typeDisplay) {
    console.warn('[notifications] dropping malformed WS notification:', raw)
    return null
  }
  if (raw.id == null) {
    // Live new_notification pushes arrive without id/request. Render them
    // read-only under a synthetic negative client id (no approve/decline —
    // there is nothing to call the API with); the next latest_notifications
    // snapshot replaces the list with the real persisted row.
    return {
      ...raw,
      id: -Date.now(),
      title: raw.title ?? '',
      created_at: raw.created_at ?? new Date().toISOString(),
      type_display: typeDisplay,
      actionable: false,
    }
  }
  return {
    ...raw,
    id: raw.id,
    title: raw.title ?? '',
    created_at: raw.created_at ?? new Date().toISOString(),
    type_display: typeDisplay,
  }
}

function normalizeList(list: RawWsNotification[]): Notification[] {
  return list.map(normalizeNotification).filter((n): n is Notification => n !== null)
}

/** Живой список уведомлений.
 *
 *  WS — единственный источник правды: REST-списка нет. Смена языка меняет
 *  `lang` и переподключает сокет, чтобы бэк прислал локализованные тексты.
 *
 *  Транспорт с 2026-08-16 — одноразовый тикет: старый `?token=<JWT>` бэк
 *  отклоняет на хендшейке (проверено — 403 против 101), из-за чего колокольчик
 *  молча переставал работать.
 */
export function useNotificationsWs(enabled: boolean) {
  const { i18n } = useTranslation()
  const backendLang = LOCALE_TO_BACKEND_HEADER[toAppLocale(i18n.language)]
  const { setNotifications, addNotification } = useNotificationsUi()
  const queryClient = useQueryClient()

  // Переподключение назначается уже после создания сокета, поэтому ссылку
  // держим в ref: сам обработчик кадров создаётся раньше.
  const reconnectRef = useRef<() => void>(() => {})
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimerRef.current) return
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null
      reconnectRef.current()
    }, RECONNECT_COALESCE_MS)
  }, [])

  /** Новое уведомление, пришедшее живым пушем (не снапшотом). */
  const receive = useCallback(
    (n: Notification) => {
      addNotification(n)
      // «Изменения подтверждены» — админ одобрил заявку на смену профиля (бэк
      // шлёт её и как `Users`, и как `Requests`).
      // Профиль участника после этого уже другой, а экран держит кеш —
      // перечитываем, иначе участник видит прежний город до перезагрузки.
      // Бэк при этом переносит регистрации на другую площадку или отменяет их —
      // перечитываем и олимпиады участника одним корнем: вкладки списка, плашку
      // главной, окна отмены и открытую деталь. Отмена с возвратом меняет ещё
      // баланс и историю платежей. Админские кеши под другими
      // ключами (`participants` во множественном числе), их это не задевает.
      if (
        n.type_display === NotificationCategory.Users ||
        n.type_display === NotificationCategory.Requests
      ) {
        void queryClient.invalidateQueries({ queryKey: QUERY_CACHE_KEYS.profile.participant })
        void queryClient.invalidateQueries({ queryKey: QUERY_CACHE_KEYS.olympiads.participantAll })
        void queryClient.invalidateQueries({
          queryKey: QUERY_CACHE_KEYS.payments.participantBalance,
        })
        void queryClient.invalidateQueries({
          queryKey: QUERY_CACHE_KEYS.payments.participantHistory,
        })
      }
    },
    [addNotification, queryClient]
  )

  const onFrame = useCallback(
    (raw: unknown) => {
      const data = raw as WsPayload | RawWsNotification[]
      if (Array.isArray(data)) {
        setNotifications(normalizeList(data))
      } else if (Array.isArray(data.latest_notifications)) {
        setNotifications(normalizeList(data.latest_notifications))
      } else if (data.notification) {
        const n = normalizeNotification(data.notification)
        if (!n) return
        receive(n)
        // Живой пуш приходит без `id` и рисуется read-only под синтетическим
        // отрицательным. Полный снапшот с настоящими id бэк отдаёт только на
        // подключении, а REST-списка нет — поэтому переподключаемся за ним.
        if (n.id < 0) scheduleReconnect()
      } else if (data.id) {
        const n = normalizeNotification(data as RawWsNotification)
        if (n) receive(n)
      }
    },
    [setNotifications, receive, scheduleReconnect]
  )

  const { reconnect } = useTicketedSocket({
    path: NOTIFICATIONS_PATH,
    enabled,
    lang: backendLang,
    onFrame,
  })

  useEffect(() => {
    reconnectRef.current = reconnect
  }, [reconnect])

  useEffect(
    () => () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    },
    []
  )
}
